// Auth API Client - Authentication utilities

import type { UserDirectoryRecord, LoginRequest, LoginResponse } from '@/types/user-directory';
import { buildGatewayNetworkError } from './api-error';
import { parseStandardApiResponse, ApiError } from './standard-api';
import { tabSync } from './tabSync';

// Auth routes use /api/v1/auth (v1 standard)
const AUTH_API_URL = (() => {
  const apiUrl = import.meta.env.VITE_API_URL?.trim() || '/api/v1';
  // If already ends with /api/v1, just append /auth
  if (apiUrl.endsWith('/api/v1')) {
    return `${apiUrl}/auth`;
  }
  // If ends with /api, append /v1/auth
  if (apiUrl.endsWith('/api')) {
    return `${apiUrl}/v1/auth`;
  }
  // Default: assume it's a base URL, add full path
  return `${apiUrl}/api/v1/auth`;
})();

// ============================================
// Authentication Token Management
// ============================================

export function getAccessToken(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem('accessToken');
}

export function setAccessToken(token: string) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem('accessToken', token);
}

export function getRefreshToken(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem('refreshToken');
}

export function setRefreshToken(token: string) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem('refreshToken', token);
}

export function clearTokens() {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem('accessToken');
  window.localStorage.removeItem('refreshToken');
  window.localStorage.removeItem('currentUser');
}

// ============================================
// User Profile Caching
// ============================================

export function cacheCurrentUser(user: UserDirectoryRecord) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem('currentUser', JSON.stringify(user));
}

export function getCachedUser(): UserDirectoryRecord | null {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem('currentUser');
  if (!raw) return null;
  try {
    return JSON.parse(raw) as UserDirectoryRecord;
  } catch {
    window.localStorage.removeItem('currentUser');
    return null;
  }
}

export function clearCachedUser() {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem('currentUser');
}

// ============================================
// Helpers
// ============================================

// Default timeouts (ms)
const LOGIN_TIMEOUT_MS = 15000;       // 15s for login/register
const CURRENT_USER_TIMEOUT_MS = 8000; // 8s for /me lookup

async function fetchWithTimeout(
  url: string,
  options: RequestInit & { timeoutMs?: number } = {}
): Promise<Response> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), options.timeoutMs ?? 10000);
  try {
    return await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        Accept: 'application/json, text/plain;q=0.9,*/*;q=0.8',
        ...(options.headers || {})
      }
    });
  } finally {
    clearTimeout(timeout);
  }
}

// ============================================
// Authentication API
// ============================================

export async function login(credentials: LoginRequest): Promise<LoginResponse> {
  const usernameOrEmail = credentials.usernameOrEmail?.trim();
  const password = credentials.password;

  if (!usernameOrEmail || !password) {
    throw new Error('Debes ingresar usuario/correo y contraseña para continuar.');
  }

  const url = `${AUTH_API_URL}/login`;
  let response: Response;

  try {
    response = await fetchWithTimeout(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username_or_email: usernameOrEmail,
        password,
      }),
      timeoutMs: LOGIN_TIMEOUT_MS
    });
  } catch (error) {
    throw buildGatewayNetworkError({
      context: 'Autenticación fallida',
      configuredUrl: AUTH_API_URL,
      envVar: 'VITE_API_URL',
      detail: error,
      suggestion: '/api/v1/auth',
    });
  }

  let data: LoginResponse;
  try {
    const parsed = await parseStandardApiResponse<LoginResponse>(response, 'Autenticación fallida');
    data = parsed.data;
  } catch (error) {
    if (error instanceof ApiError) {
      if (error.status === 401 || error.status === 403 || error.code === 'INVALID_CREDENTIALS') {
        throw new Error(error.message || 'Credenciales inválidas. Verifica tu correo y contraseña.');
      }
      if ((error.status ?? 0) >= 500) {
        throw new Error(
          error.message ||
            'El servicio de autenticación respondió con un error inesperado. Intenta nuevamente en unos minutos.'
        );
      }
      throw error;
    }
    throw error;
  }

  if (!data.accessToken || !data.refreshToken) {
    throw new Error('El servicio no entregó tokens válidos. Comunícate con soporte.');
  }

  // Store tokens and cache user
  setAccessToken(data.accessToken);
  setRefreshToken(data.refreshToken);
  cacheCurrentUser(data.user as unknown as UserDirectoryRecord);

  return data;
}

export async function register(userData: any): Promise<LoginResponse> {
  const url = `${AUTH_API_URL}/register`;
  let response: Response;

  try {
    response = await fetchWithTimeout(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
      timeoutMs: LOGIN_TIMEOUT_MS
    });
  } catch (error) {
    throw buildGatewayNetworkError({
      context: 'Registro de usuario fallido',
      configuredUrl: AUTH_API_URL,
      envVar: 'VITE_API_URL',
      detail: error,
      suggestion: '/api/v1/auth',
    });
  }

  const { data } = await parseStandardApiResponse<LoginResponse>(
    response,
    'Registro de usuario fallido'
  );

  // Store tokens and cache user
  setAccessToken(data.accessToken);
  setRefreshToken(data.refreshToken);
  cacheCurrentUser(data.user as unknown as UserDirectoryRecord);

  return data;
}

export async function logout() {
  // Revoke refresh token on backend before clearing local state
  const refreshToken = getRefreshToken();
  const accessToken = getAccessToken();
  if (refreshToken) {
    try {
      await fetch(`${AUTH_API_URL}/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
    } catch {
      // Ignore network errors - still clear local tokens
    }
  }

  clearTokens();
  clearCachedUser();

  // PROJ-035 Enhancement: Broadcast logout to other tabs
  tabSync.broadcast({
    type: 'USER_LOGGED_OUT',
    timestamp: Date.now()
  });
}

export async function getCurrentUser(): Promise<UserDirectoryRecord> {
  const url = `${AUTH_API_URL}/me`;
  let response: Response;
  try {
    response = await fetchWithTimeout(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(getAccessToken() ? { Authorization: `Bearer ${getAccessToken()}` } : {}),
      },
      timeoutMs: CURRENT_USER_TIMEOUT_MS
    });
  } catch (error) {
    throw buildGatewayNetworkError({
      context: 'No fue posible obtener el usuario actual',
      configuredUrl: AUTH_API_URL,
      envVar: 'VITE_API_URL',
      detail: error,
      suggestion: '/api/v1/auth',
    });
  }

  const { data: user } = await parseStandardApiResponse<UserDirectoryRecord>(
    response,
    'No fue posible obtener el usuario actual'
  );
  cacheCurrentUser(user);

  return user;
}
